<template>
  <div class="country-phone-input">
    <div class="d-flex align-center country-phone-input__row">
      <v-menu
        v-if="!lockedDialCode"
        offset-y
        :close-on-content-click="false"
        v-model="menuOpen"
        :nudge-bottom="4"
        :max-width="320"
        content-class="country-phone-menu"
      >
        <v-btn
          slot="activator"
          flat
          small
          class="country-trigger px-2"
          style="text-transform: none; min-width: 100px;"
          type="button"
        >
          <flag
            :iso="selectedCountry.iso"
            :squared="false"
            class="mr-1"
            style="width: 22px; height: 16px;"
          />
          <span style="font-size: 14px; font-weight: 500;">{{ selectedCountry.dialCode }}</span>
          <v-icon small class="ml-1">arrow_drop_down</v-icon>
        </v-btn>

        <v-card>
          <v-card-text class="pa-2" style="overflow: visible;">
            <v-text-field
              v-model="search"
              :label="$t('phoneInput.searchCountryLabel')"
              prepend-inner-icon="search"
              autofocus
              hide-details
              clearable
              dense
            />
          </v-card-text>
          <v-list dense class="country-phone-menu__list">
            <v-list-tile
              v-for="country in filteredCountries"
              :key="country.dialCode + country.iso"
              @click="selectCountry(country)"
            >
              <v-list-tile-action style="min-width: 36px;">
                <flag
                  :iso="country.iso"
                  :squared="false"
                  style="width: 22px; height: 16px;"
                />
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>
                  <span style="font-weight: 500;">{{ country.dialCode }}</span>
                  <span class="ml-2">{{ country.name }}</span>
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-tile v-if="filteredCountries.length === 0">
              <v-list-tile-content>
                <v-list-tile-title class="text-center">
                  {{ $t('phoneInput.noMatches') }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card>
      </v-menu>

      <div
        v-else
        class="country-trigger country-trigger--static px-2"
      >
        <flag
          :iso="selectedCountry.iso"
          :squared="false"
          class="mr-1"
          style="width: 22px; height: 16px;"
        />
        <span style="font-size: 14px; font-weight: 500;">{{ selectedCountry.dialCode }}</span>
      </div>

      <v-text-field
        v-model="localDigits"
        :label="label"
        :placeholder="placeholder"
        :error-messages="computedErrors"
        :maxlength="selectedCountry.max"
        :disabled="disabled"
        type="tel"
        class="ml-2 flex-grow-1"
        @input="onLocalInput"
        @blur="touched = true"
      />
    </div>
  </div>
</template>

<script>
import {
  COUNTRIES,
  FALLBACK_MIN,
  FALLBACK_MAX,
  splitPhoneByCountry,
  findCountryByDialCode
} from '../../data/phoneLengths'

const FALLBACK_COUNTRY = {
  dialCode: '+964',
  name: 'Iraq',
  iso: 'iq',
  min: FALLBACK_MIN,
  max: FALLBACK_MAX
}

export default {
  name: 'CountryPhoneInput',
  props: {
    value: { type: String, default: '' },
    defaultDialCode: { type: String, default: '+964' },
    label: { type: String, default: 'Phone Number' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    lockedDialCode: { type: String, default: '' },
    required: { type: Boolean, default: true }
  },
  data() {
    return {
      menuOpen: false,
      search: '',
      selectedCountry: FALLBACK_COUNTRY,
      localDigits: '',
      touched: false
    }
  },
  computed: {
    filteredCountries() {
      const q = (this.search || '').trim().toLowerCase()
      if (!q) return COUNTRIES
      return COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.dialCode.includes(q) ||
          (c.iso || '').includes(q)
      )
    },
    isValid() {
      const len = this.localDigits.length
      if (len === 0) return !this.required
      return len >= this.selectedCountry.min && len <= this.selectedCountry.max
    },
    emittedValue() {
      if (!this.localDigits) return ''
      return this.isValid ? `${this.selectedCountry.dialCode}${this.localDigits}` : ''
    },
    computedErrors() {
      if (this.errorMessage) return [this.errorMessage]
      if (!this.touched) return []
      if (!this.localDigits) {
        return this.required ? [this.$t('phoneInput.phoneRequiredError')] : []
      }
      if (!this.isValid) return [this.$t('phoneInput.phoneNotCorrectError')]
      return []
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.syncFromValue(newVal)
      }
    },
    isValid(newVal) {
      this.$emit('update:isValid', newVal)
    }
  },
  mounted() {
    this.$emit('update:isValid', this.isValid)
  },
  methods: {
    syncFromValue(value) {
      const incoming = value || ''
      if (incoming === this.emittedValue) return

      if (this.lockedDialCode) {
        const locked = findCountryByDialCode(this.lockedDialCode) || FALLBACK_COUNTRY
        this.selectedCountry = locked
        if (!incoming) {
          this.localDigits = ''
          return
        }
        this.localDigits = incoming.startsWith(locked.dialCode)
          ? incoming.slice(locked.dialCode.length).replace(/\D/g, '')
          : incoming.replace(/\D/g, '')
        return
      }

      if (!incoming) {
        const def =
          findCountryByDialCode(this.defaultDialCode) || FALLBACK_COUNTRY
        this.selectedCountry = def
        this.localDigits = ''
        return
      }

      const split = splitPhoneByCountry(incoming)
      if (split) {
        this.selectedCountry = split.country
        this.localDigits = split.localDigits.replace(/\D/g, '')
      } else {
        this.localDigits = incoming.replace(/\D/g, '')
      }
    },
    resetTouched() {
      this.touched = false
    },
    selectCountry(country) {
      if (this.lockedDialCode) return
      const sameCountry = country.iso === this.selectedCountry.iso
      this.selectedCountry = country
      this.menuOpen = false
      this.search = ''
      if (!sameCountry) {
        this.localDigits = ''
        this.touched = false
      }
      this.emitChange()
    },
    onLocalInput(val) {
      const cleaned = (val || '').replace(/\D/g, '').slice(0, this.selectedCountry.max)
      if (cleaned !== this.localDigits) {
        this.localDigits = cleaned
      }
      this.emitChange()
    },
    emitChange() {
      this.$emit('input', this.emittedValue)
    }
  }
}
</script>

<style scoped>
.country-phone-input {
  width: 100%;
  overflow: visible;
}
.country-phone-input__row {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: flex-end !important;
  overflow: visible;
}
/* The v-menu root element sits directly in the flex row — keep it shrunk */
.country-phone-input__row >>> .v-menu {
  flex: 0 0 auto;
  display: inline-flex !important;
  align-items: flex-end;
}
.country-phone-input__row >>> .v-menu__activator {
  display: inline-flex !important;
  align-items: flex-end;
}
.country-trigger {
  border: 1px solid rgba(0, 0, 0, 0.42) !important;
  border-radius: 4px !important;
  height: 42px !important;
  min-height: 42px !important;
  margin: -60px 0 6px 0 !important;
  padding: 0 8px !important;
  display: inline-flex !important;
  flex-shrink: 0;
}
.country-trigger--static {
  margin: 0 0 6px 0 !important;
  align-items: center;
  cursor: default;
}
</style>

<!-- Menu DOM is teleported to [data-app]; selectors must be unscoped -->
<style>
.country-phone-menu {
  overflow: visible !important;
}
.country-phone-menu .country-phone-menu__list {
  max-height: 320px;
  overflow-y: auto;
}

/* Prevent Vuetify v-btn from overriding our height / display */
.country-trigger.v-btn {
  display: inline-flex !important;
  height: 42px !important;
  min-height: 42px !important;
  overflow: visible !important;
}
.country-trigger.v-btn .v-btn__content {
  display: inline-flex !important;
  align-items: center !important;
  height: 100% !important;
  overflow: visible !important;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/CountryPhoneInput.vue